interface TrailerToolbarProps {
  jobId: string
  jobNumber?: string
  isSaving: boolean
  isArranging: boolean
  isDirty: boolean
  driverEditPermission?: any
  onToggleDriverPermission?: (unlock: boolean) => Promise<void>
  isDriver?: boolean
  onSave: () => void
  onAutoArrange: () => void
  onReset: () => void
}

export function TrailerToolbar({ jobId, jobNumber, isSaving, isArranging, isDirty, driverEditPermission, onToggleDriverPermission, isDriver, onSave, onAutoArrange, onReset }: TrailerToolbarProps) {
  const jobIdShort = typeof jobId === 'string' && jobId.length > 0 ? jobId.slice(0, 8) : 'UNKNOWN'

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border p-3 shadow-sm">
      <div>
        <div className="font-semibold text-gray-900 text-sm">
          Trailer Planner {jobNumber ? `— ${jobNumber}` : ''}
        </div>
        <div className="text-xs text-gray-500 font-mono">Job {jobIdShort}</div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {driverEditPermission && (
          <div className="text-xs text-gray-600 px-2 py-1 border rounded-md bg-gray-50">
            <strong>Driver Edit:</strong> {driverEditPermission.unlocked ? 'Unlocked' : 'Locked'}
          </div>
        )}

        {/* Admin controls for toggling driver permission */}
        {!isDriver && typeof onToggleDriverPermission === 'function' && (
          <>
            <button
              type="button"
              onClick={() => onToggleDriverPermission(true)}
              disabled={isSaving || isArranging || (driverEditPermission && driverEditPermission.unlocked)}
              className="min-h-[44px] px-3 py-2 rounded-lg border border-green-300 bg-green-50 text-green-800 text-sm font-medium hover:bg-green-100 disabled:opacity-50 transition-colors"
            >
              Unlock Driver Editing
            </button>
            <button
              type="button"
              onClick={() => onToggleDriverPermission(false)}
              disabled={isSaving || isArranging || !(driverEditPermission && driverEditPermission.unlocked)}
              className="min-h-[44px] px-3 py-2 rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm font-medium hover:bg-red-100 disabled:opacity-50 transition-colors"
            >
              Revoke Driver Editing
            </button>
            <div className="text-xs text-gray-500">Allows assigned driver to move vehicles and save layout. Does not expose finance.</div>
          </>
        )}
        {!isDriver && (
          <>
            <button
              type="button"
              onClick={onAutoArrange}
              disabled={isArranging || isSaving}
              className="flex min-h-[44px] items-center gap-1.5 px-3 py-2 rounded-lg border border-purple-300 bg-purple-50 text-purple-800 text-sm font-medium hover:bg-purple-100 disabled:opacity-50 transition-colors"
            >
              {isArranging ? (
                <><span className="animate-spin">⟳</span> Arranging…</>
              ) : (
                <>⚡ Auto Arrange</>
              )}
            </button>

            <button
              type="button"
              onClick={onReset}
              disabled={isSaving || isArranging || !isDirty}
              className="min-h-[44px] px-3 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              ↺ Reset
            </button>

            <button
              type="button"
              onClick={onSave}
              disabled={isSaving || isArranging}
              className="flex min-h-[44px] items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 disabled:opacity-60 transition-colors"
            >
              {isSaving ? (
                <><span className="animate-spin">⟳</span> Saving…</>
              ) : (
                <>💾 Save Layout</>
              )}
            </button>
          </>
        )}

        {isDirty && !isSaving && (
          <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
        )}
      </div>
    </div>
  )
}
